import { Dialog } from '@angular/cdk/dialog';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
  CSP_NONCE,
  inject,
  Injectable,
  NgZone,
  PLATFORM_ID,
  RendererFactory2,
} from '@angular/core';
import {
  combineLatest,
  defer,
  filter,
  map,
  ReplaySubject,
  shareReplay,
  switchMap,
  take,
  tap,
  timeout,
  timer,
} from 'rxjs';
import { ExternalScriptConfig, WAF_CONFIG } from './waf.token';
import { WafComponent } from './waf.component';

interface CaptchaError extends Error {
  kind: 'internal_error' | 'network_error' | 'token_error' | 'client_error';
  statusCode?: number;
}

interface WafCaptchaConfiguration {
  apiKey: string;
  onSuccess: (wafToken: string) => void;
  onError?: (error: CaptchaError) => void;
  onLoad?: () => void;
  onPuzzleTimeout?: () => void;
  onPuzzleCorrect?: () => void;
  onPuzzleIncorrect?: () => void;
  defaultLocale?: string;
  disableLanguageSelector?: boolean;
  dynamicWidth?: boolean;
  skipTitle?: boolean;
}

export interface WafWindow extends Window {
  AwsWafIntegration: {
    hasToken: () => boolean;
    getToken: () => Promise<string>;
  };
  AwsWafCaptcha: {
    renderCaptcha: (
      container: HTMLElement,
      configuration: WafCaptchaConfiguration
    ) => void;
  };
  awsWafCookieDomainList?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class WafService {
  private readonly _renderer = inject(RendererFactory2).createRenderer(
    null,
    null
  );
  private readonly _wafConfig = inject(WAF_CONFIG);
  private readonly _dialog = inject(Dialog);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _document = inject(DOCUMENT);
  private nonce = inject(CSP_NONCE);
  private readonly _isLoaded$ = this.areScriptsLoaded();
  private readonly _wafWindow = this._isLoaded$.pipe(
    map(() => this._document.defaultView as any as WafWindow),
    tap((wafWindow) => {
      if (!wafWindow.awsWafCookieDomainList) {
        (window as any).awsWafCookieDomainList = ['sight-sound.com'];
      }
    }),
    shareReplay(1)
  );
  private ngZone = inject(NgZone);

  private areScriptsLoaded() {
    return combineLatest(
      this._wafConfig.scripts.map((script) => this.loadScript(script))
    );
  }

  getToken() {
    return this._wafWindow.pipe(
      switchMap((wafWindow) =>
        defer(() => wafWindow.AwsWafIntegration.getToken())
      )
    );
  }

  renderCaptcha() {
    const dialogRef = this._dialog.open(WafComponent);
    const waitForTemplate = dialogRef.componentInstance!.wafOutlet$.pipe(
      filter((outlet) => !!outlet),
      map((outlet) => outlet.nativeElement!)
    );
    return combineLatest([this._wafWindow, waitForTemplate]).pipe(
      switchMap(([wafWindow, wafOutlet]) => {
        const success = new ReplaySubject<string>(1);
        wafWindow.AwsWafCaptcha.renderCaptcha(wafOutlet, {
          apiKey: this._wafConfig.token,
          onSuccess: (token) => {
            success.next(token);
            success.complete();
            dialogRef.close();
          },
          onError: (error) => {
            success.error(error);
            success.complete();
            dialogRef.close();
          },
        });
        return success;
      })
    );
  }

  private loadScript(scriptConfig: ExternalScriptConfig) {
    const loaded = new ReplaySubject<void>(1);
    const element = this._document.querySelector(`#${scriptConfig.id}`);
    if (isPlatformServer(this._platformId)) {
      loaded.next();
      loaded.complete();
      return loaded;
    }
    if (element) {
      loaded.next();
      loaded.complete();
      return loaded;
    }
    const script = this._renderer?.createElement('script');
    if (!script) {
      loaded.error('Could not create script element');
      loaded.complete();
      return loaded;
    }
    script.id = scriptConfig.id;
    script.src = scriptConfig.url;
    script.nonce = this.nonce;
    script.onload = () => {
      this.pollForWindowObject(scriptConfig.windowObjectNames, loaded);
    };
    script.onerror = (error: ErrorEvent) => {
      console.error(`Error loading script: ${scriptConfig.url}`, error);
      loaded.next();
    };
    this._renderer?.appendChild(this._document.body, script);
    return loaded;
  }

  private pollForWindowObject(
    names: string[],
    subject: ReplaySubject<void>,
    interval = 100,
    maxAttempts = 30
  ) {
    this.ngZone.runOutsideAngular(() => {
      timer(0, interval)
        .pipe(
          map(() => names.every((windowName) => (window as any)[windowName])),
          filter(Boolean),
          take(1),
          timeout(maxAttempts * interval)
        )
        .subscribe({
          next: () => {
            this.ngZone.run(() => {
              subject.next();
              subject.complete();
            });
          },
          error: () => {
            this.ngZone.run(() => {
              subject.error(
                `Window object '${names.join(', ')}' not found after ${maxAttempts} attempts`
              );
              subject.complete();
            });
          },
        });
    });
  }
}
